import React from "react";
import {
  View,
  ScrollView,
  TouchableOpacity,
  Image} from "react-native";
import AntDesign from "@expo/vector-icons/AntDesign";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { Linking } from "react-native";
import commonStyles from "../../styles/commonStyles";
import OverflowTooltipText from "../../components/OverflowTooltipText";
import { ShareholderTransactionListModel } from "../../types/IPayment";
import {
  formatDate,
  inferShareholderTransactionType,
  inferTransactionType} from "../../common/utils";
import { SafeAreaView } from "react-native-safe-area-context";
import AppText from "../../components/AppText";

type PropertyDetailsProps = {
  navigation: NativeStackNavigationProp<any>;
  route: {
    params: {
      payment: ShareholderTransactionListModel;
      sourcePage?: string;
    };
  };
};

const PaymentDetails: React.FC<PropertyDetailsProps> = ({
  navigation,
  route}) => {
  const { payment } = route.params;

  const openLink = async (url: string) => {
    const supported = await Linking.canOpenURL(url);
    if (supported) {
      await Linking.openURL(url);
    } else {
      console.log("Don't know how to open URI: " + url);
    }
  };

  return (
    <SafeAreaView edges={[ "bottom", "left", "right"]} style={[commonStyles.flex1, commonStyles.screenBottomGap]}>
      <View style={commonStyles.container}>
        <TouchableOpacity
          onPress={() => navigation.goBack()}
          style={commonStyles.subHeaderContainer}
        >
          <AntDesign testID="back-arrow" name="left" size={24} color="black" />
          <OverflowTooltipText
            testID=""
            text={payment.entityName}
            style={commonStyles.subHeader}
            onPress={() => navigation.goBack()}
          />
        </TouchableOpacity>
        <ScrollView testID="scrollView">
          <View
            style={[
              commonStyles.subContainer,
              commonStyles.p14,
              commonStyles.mlar2,
              commonStyles.mt5,
              commonStyles.pt0,
              commonStyles.mb10,
            ]}
          >
            <View style={commonStyles.subDetails}>
              <AppText
                testID={`shareholder-label`}
                style={[commonStyles.subTitle, { marginRight: 20 }]}
              >
                Shareholder
              </AppText>
              <View style={commonStyles.subValue}>
                <OverflowTooltipText
                  testID="ShareholderName"
                  text={payment.shareholderName}
                  style={commonStyles.valueText}
                />
              </View>
            </View>
            <View style={commonStyles.subDetails}>
              <AppText testID={`date-label`} style={commonStyles.subTitle}>
                Date
              </AppText>
              <View style={commonStyles.subValue}>
                <AppText
                  testID={`date-${payment.transaction.id}`}
                  style={commonStyles.valueText}
                >
                  {formatDate(payment.transaction.date)}
                </AppText>
              </View>
            </View>
            <View style={commonStyles.subDetails}>
              <AppText testID={`amount-label`} style={commonStyles.subTitle}>
                Amount
              </AppText>
              <AppText
                testID={`amount-${payment.transaction.id}`}
                style={commonStyles.subValue}
              >
                $
                {Number(payment.transaction.amount).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2})}
              </AppText>
            </View>
            <View style={commonStyles.subDetails}>
              <AppText testID={`transaction-label`} style={commonStyles.subTitle}>
                Transaction
              </AppText>
              <AppText
                testID={`transaction-${payment.transaction.id}`}
                style={commonStyles.subValue}
              >
                {inferTransactionType(payment.transaction)}
              </AppText>
            </View>

            <View style={commonStyles.subDetails}>
              <AppText testID={`type-label`} style={commonStyles.subTitle}>
                Type
              </AppText>
              <AppText
                testID={`type-${payment.transaction.id}`}
                style={commonStyles.subValue}
              >
                {inferShareholderTransactionType(payment.transaction)}
              </AppText>
            </View>

            <View style={commonStyles.subDetails}>
              <AppText testID={`link-label`} style={commonStyles.subTitle}>
                Link
              </AppText>

              {payment.transaction.urls?.[0] && (
                <TouchableOpacity
                  onPress={() => openLink(payment.transaction.urls[0])}
                  style={commonStyles.subValue}
                >
                  <Image
                    source={require("../../../assets/googledrive.png")}
                    style={{ width: 24, height: 24 }}
                    resizeMode="contain"
                  />
                </TouchableOpacity>
              )}
            </View>
            <View style={commonStyles.columnContainer}>
              <AppText testID={`description-label`} style={commonStyles.subTitle}>
                Description
              </AppText>
              <AppText
                testID={`description-${payment.transaction.id}`}
                numberOfLines={8}
                ellipsizeMode="tail"
                style={[
                  commonStyles.subValue,
                  { flexShrink: 1, maxWidth: "100%", textAlign: "left" },
                ]}
              >
                {payment.transaction.description}
              </AppText>
            </View>
          </View>
        </ScrollView>
      </View>
    </SafeAreaView>
  );
};

export default PaymentDetails;
